import { Alert } from '@mui/material';
import { subMonths } from 'date-fns';
import { useMemo, useState } from 'react';
import { Loader } from '../../../components/Loader/Loader';
import { CompanyPeriodRequestStatus } from '../../../schemas/CompanyPeriodRequest.schema';
import { NoDataMessage2 } from '../../CompanyProfiles/components/Messages/HelpMessage';
import { usePortCos } from '../../Finance2/hooks/usePortCos';
import { CompanyPeriodRequestManage } from './CompanyPeriodRequestManage';
import { useCompanyPeriodRequestData } from './company-period-request-data';
import {
  DefaultDateRangeMonths,
  getAllCompanyPeriodRequestStatuses,
  getRequestsToShow,
  mapCompanyPeriodRequestsToViewModel,
} from './manage-request-utils';

export function CompanyPeriodRequestManageLoader() {
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | undefined>(undefined);
  const [selectedDates, setSelectedDates] = useState<[Date, Date]>(() => {
    const now = new Date();
    return [subMonths(now, DefaultDateRangeMonths), now];
  });

  const { loading: loadingPortcos, error: portcosError, data: portcos } = usePortCos();
  const {
    isLoading: isLoadingCompanyPeriodRequests,
    error: companyPeriodRequestsError,
    data: requestResponse,
  } = useCompanyPeriodRequestData();

  const allStatuses = useMemo(() => getAllCompanyPeriodRequestStatuses(), []);

  const onDateRangeChange = (from: Date | null, to: Date | null) => {
    if (from && to) {
      setSelectedDates([from, to]);
    }
  };

  const { includedCompanies, requests, requestStats } = useMemo(() => {
    if (portcos && requestResponse?.success) {
      return mapCompanyPeriodRequestsToViewModel({
        requests: getRequestsToShow(requestResponse.requests),
        companies: portcos,
        from: selectedDates[0],
        to: selectedDates[1],
        selectedCompanyId,
      });
    }
    return {
      includedCompanies: new Map<number, string>(),
      requests: [],
      requestStats: new Map<CompanyPeriodRequestStatus | string, number>(),
    };
  }, [requestResponse, portcos, selectedCompanyId, selectedDates]);

  if (loadingPortcos || isLoadingCompanyPeriodRequests) {
    return <Loader />;
  }

  if (portcosError || companyPeriodRequestsError || !requestResponse?.success) {
    return (
      <Alert severity='error' sx={{ width: '100%', height: 'fit-content' }}>
        Error loading data.
      </Alert>
    );
  }

  if (!portcos || !requestResponse) {
    return <NoDataMessage2 />;
  }

  return (
    <CompanyPeriodRequestManage
      allStatuses={allStatuses}
      onDateRangeChange={onDateRangeChange}
      includedCompanies={includedCompanies}
      onSelectedCompanyChange={setSelectedCompanyId}
      requests={requests}
      requestStats={requestStats}
      selectedCompanyId={selectedCompanyId}
      selectedDates={selectedDates}
    />
  );
}
